import { invoicePaymentMethodLabels } from '@/constants'
import { InvoicePayment } from '@/types'
import { formatCurrency } from '@/utils/format'
import { ActionIcon, Badge, Table, Text, Tooltip } from '@mantine/core'
import { IconTrash } from '@tabler/icons-react'
import dayjs from 'dayjs'

function reportBadge(payment: InvoicePayment, reportsPayments: boolean) {
  if (payment.cancelled_at) return { label: 'Annulé', color: 'gray' }
  if (!reportsPayments) return null
  if (payment.reported_at) return { label: 'Déclaré à la plateforme', color: 'green' }
  if (payment.last_error) return { label: 'Déclaration en attente', color: 'orange' }
  return { label: 'Déclaration en cours', color: 'blue' }
}

function PaymentRow({
  payment,
  reportsPayments,
  onCancel,
}: {
  payment: InvoicePayment
  reportsPayments: boolean
  onCancel: (payment: InvoicePayment) => void
}) {
  const handleCancel = () => onCancel(payment)
  const badge = reportBadge(payment, reportsPayments)

  return (
    <Table.Tr c={payment.cancelled_at ? 'dimmed' : undefined}>
      <Table.Td>{dayjs(payment.paid_on).format('DD/MM/YYYY')}</Table.Td>
      <Table.Td>{invoicePaymentMethodLabels[payment.payment_method]}</Table.Td>
      <Table.Td ta="right" td={payment.cancelled_at ? 'line-through' : undefined}>
        {formatCurrency(payment.amount)}
      </Table.Td>
      <Table.Td>
        {badge && (
          <Tooltip label={payment.last_error} disabled={!payment.last_error} withArrow multiline w={280}>
            <Badge variant="light" color={badge.color}>
              {badge.label}
            </Badge>
          </Tooltip>
        )}
      </Table.Td>
      <Table.Td ta="right">
        {!payment.cancelled_at && (
          <Tooltip label="Annuler ce paiement" withArrow>
            <ActionIcon variant="subtle" color="red" onClick={handleCancel} aria-label="Annuler ce paiement">
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        )}
      </Table.Td>
    </Table.Tr>
  )
}

export default function InvoicePaymentsTable({
  payments,
  reportsPayments,
  onCancel,
}: {
  payments: InvoicePayment[]
  reportsPayments: boolean
  onCancel: (payment: InvoicePayment) => void
}) {
  if (payments.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        Aucun paiement enregistré.
      </Text>
    )
  }

  return (
    <Table>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Reçu le</Table.Th>
          <Table.Th>Moyen</Table.Th>
          <Table.Th ta="right">Montant</Table.Th>
          <Table.Th />
          <Table.Th w={50} />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {payments.map((payment) => (
          <PaymentRow key={payment.id} payment={payment} reportsPayments={reportsPayments} onCancel={onCancel} />
        ))}
      </Table.Tbody>
    </Table>
  )
}
